import type { CallbackAction, GeolocationCallback } from '@fanduel/geolocation-sdk';
import type { Session } from '@fanduel/cpe-um-bundle/account/contract';
import type { Experiment } from '@/types/aggregation/home';
import type { GeolocationConfig } from '@/types/config';
import { getConfig } from '@/utils/config/config';
import { COOKIES } from '@/utils/cookies';
import { AMPLITUDE_EXPERIMENTS, isExperimentEnabled } from '@/utils/experiments';
import {
    beginLoginGeolocation,
    endLoginGeolocation,
    resetLoginGeolocationGate
} from '@/logic/geolocation/login-geolocation-gate';
import { clearLoginGeoPill, setLoginGeoPillTransientResult } from '@/logic/geolocation/login-geo-pill-state';
import buildProviderConfig from '@/logic/geolocation/geolocation-config';

import { loginGeoRum } from '@/logic/geolocation/login-geo-rum';
import { GeoLogRumTypes, LocationVerificationPillVisual } from '@/logic/geolocation/login-geo-types.types';
import type { Region } from '@/types/constants';

type GeolocationSdkModule = Awaited<ReturnType<typeof loadGeolocationSdk>>;

const LOGIN_LOCATE_REASON = 'Login';
const loadGeolocationSdk = () => import('@fanduel/geolocation-sdk');

let geolocationSdkPromise: Promise<GeolocationSdkModule> | null = null;

const getGeolocationSdk = (): Promise<GeolocationSdkModule> => {
    if (!geolocationSdkPromise) {
        geolocationSdkPromise = loadGeolocationSdk();
    }
    return geolocationSdkPromise;
};

export const prefetchLoginGeolocationSdk = (): Promise<GeolocationSdkModule> => getGeolocationSdk();

export type LoginGeolocationInput = {
    experiments: Experiment;
    isMobileWebLobby: boolean;
    session: Pick<Session, 'sessionId' | 'token'>;
    userId: string;
    region: Region;
};

let lastGeolocationSessionId: string | null = null;

const hasExistingGeocomplyToken = (): boolean => Boolean(localStorage.getItem(COOKIES.X_GEOCOMPLY_TOKEN)?.trim());

export const resetLoginGeolocationDedupe = (): void => {
    lastGeolocationSessionId = null;
    resetLoginGeolocationGate();
    clearLoginGeoPill();
};

const buildLoginGeolocationCallback = (sdk: GeolocationSdkModule, silent: boolean): GeolocationCallback => {
    const {
        GEOLOCATION_INVALIDATED,
        GEOLOCATION_PASSED,
        GEOLOCATION_REJECTED,
        LOCATION_FAILURE,
        LOCATION_IN_PROGRESS
    } = sdk;

    return (action: CallbackAction) => {
        switch (action.type) {
            case LOCATION_IN_PROGRESS:
                if (!silent) {
                    beginLoginGeolocation();
                }
                loginGeoRum(GeoLogRumTypes.PROGRESS, {
                    region: action.payload?.region,
                    product: action.payload?.product,
                    silent
                });
                break;
            case GEOLOCATION_PASSED:
                if (!silent) {
                    endLoginGeolocation(LocationVerificationPillVisual.SUCCESS);
                    setLoginGeoPillTransientResult(LocationVerificationPillVisual.SUCCESS);
                }

                loginGeoRum(GeoLogRumTypes.PASSED, {
                    region: action.payload?.region,
                    product: action.payload?.product,
                    silent
                });

                if (action.payload.jwt) {
                    localStorage.setItem(COOKIES.X_GEOCOMPLY_TOKEN, action.payload.jwt);
                }
                break;
            case GEOLOCATION_REJECTED:
                if (!silent) {
                    endLoginGeolocation(LocationVerificationPillVisual.FAILED);
                    setLoginGeoPillTransientResult(LocationVerificationPillVisual.FAILED);
                }

                loginGeoRum(GeoLogRumTypes.REJECTED, {
                    region: action.payload?.region,
                    product: action.payload?.product,
                    silent
                });
                break;
            case LOCATION_FAILURE:
                if (!silent) {
                    endLoginGeolocation(LocationVerificationPillVisual.FAILED);
                    setLoginGeoPillTransientResult(LocationVerificationPillVisual.FAILED);
                }
                loginGeoRum(GeoLogRumTypes.FAILED, {
                    region: action.payload?.region,
                    product: action.payload?.product,
                    silent
                });
                break;
            case GEOLOCATION_INVALIDATED:
                if (!silent) {
                    endLoginGeolocation(LocationVerificationPillVisual.FAILED);
                    setLoginGeoPillTransientResult(LocationVerificationPillVisual.FAILED);
                }
                loginGeoRum(GeoLogRumTypes.INVALIDATED, { silent });

                break;
            default:
                break;
        }
    };
};

const runWebLoginLocate = async (input: LoginGeolocationInput, geo: GeolocationConfig): Promise<void> => {
    const silent = hasExistingGeocomplyToken();

    try {
        if (!silent) {
            beginLoginGeolocation();
        }

        const sdk = await getGeolocationSdk();
        const { Geolocation } = sdk;

        Geolocation.init(geo.serviceName, process.env.BUILD_VERSION ?? 'unknown');
        loginGeoRum(GeoLogRumTypes.INIT, { region: input.region, product: process.env.PRODUCT });

        Geolocation.setCallback(buildLoginGeolocationCallback(sdk, silent));
        loginGeoRum(GeoLogRumTypes.CALLBACK_SET, { region: input.region, product: process.env.PRODUCT });

        await Geolocation.setConfig(
            buildProviderConfig(geo, {
                region: input.region,
                sessionId: input.session.sessionId,
                userId: input.userId,
                userAuthToken: input.session.token
            })
        );
        loginGeoRum(GeoLogRumTypes.CONFIG_SET, { region: input.region, product: process.env.PRODUCT });

        Geolocation.locateUser(LOGIN_LOCATE_REASON);
    } catch (e) {
        if (!silent) {
            endLoginGeolocation(LocationVerificationPillVisual.FAILED);
            setLoginGeoPillTransientResult(LocationVerificationPillVisual.FAILED);
        }
        loginGeoRum(GeoLogRumTypes.FAILED, { region: input.region, product: process.env.PRODUCT });
        throw e;
    }
};

export const maybeRunLoginGeolocation = (input: LoginGeolocationInput): void => {
    if (!input.isMobileWebLobby || !isExperimentEnabled(input.experiments, AMPLITUDE_EXPERIMENTS.GEOLOCATION_LOGIN)) {
        return;
    }

    if (!input.userId || !input.region.trim()) {
        return;
    }

    if (input.session.sessionId === lastGeolocationSessionId) {
        return;
    }

    lastGeolocationSessionId = input.session.sessionId;

    const geo = getConfig().geolocation;
    runWebLoginLocate(input, geo);
};
